<script setup>
import { tv } from 'tailwind-variants';
import { useAttrs, useListeners } from 'vue';

/**
 * @typedef {object} CardProps
 * @property {string} [tag='div'] - HTML element tag to use for the card
 * @property {string} [className=''] - Additional CSS classes to apply and merge with the default classes
 * @property {('default'|'xl'|'2xl'|'2.5xl')} [radius='default'] - Border radius size of the card
 */

/**
 * @type {CardProps}
 */
const props = defineProps({
  tag: {
    type: String,
    default: 'div',
  },
  className: {
    type: String,
    default: '',
  },
  radius: {
    type: String,
    default: 'default',
  },
  variantColor: {
    type: String,
    default: 'default',
  },
});

const cardRecipe = tv({
  base: ['z-0 inline-flex relative bg-brand-background'],
  variants: {
    radius: {
      default: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '2.5xl': 'rounded-[20px]',
    },
    variantColor: {
      default: [
        'before:content-[""] before:absolute before:inset-0 before:z-10',
        'before:pointer-events-none',
        'before:mix-blend-luminosity before:shadow-brand-bevel-100',
        'dark:shadow-none dark:before:hidden dark:border dark:border-brand-border',
      ],
      magic: ['border border-brand-purple-11'],
    },
  },
  compoundVariants: [
    {
      variantColor: 'default',
      radius: 'default',
      className: 'before:rounded-lg',
    },
    {
      variantColor: 'default',
      radius: 'xl',
      className: 'before:rounded-xl',
    },
    {
      variantColor: 'default',
      radius: '2xl',
      className: 'before:rounded-2xl',
    },
    {
      variantColor: 'default',
      radius: '2.5xl',
      className: 'before:rounded-[20px]',
    },
  ],
  defaultVariants: {
    radius: 'default',
    variantColor: 'default',
  },
});

const attrs = useAttrs();
const listeners = useListeners();
</script>

<template>
  <component
    :is="props.tag"
    data-component="CsCard"
    :data-radius="props.radius"
    :data-variant-color="props.variantColor"
    :class="cardRecipe(props, { className: props.className })"
    v-bind="attrs"
    v-on="listeners"
  >
    <slot />
  </component>
</template>
